import { ArtisModuleHost, useLayoutProducer } from "@artis/sdk-react";
import { cn } from "mcn";
import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SCRATCHPAD_MODULE } from "@package/scratchpad-utils";

import { useSessionStorageState } from "../utils/react/useSessionStorageState";
import { MARKET_GRID_MODULE } from "../artis-modules";
import { computeLayout, type DividerInfo, type Rect } from "./multi-pane/layout";
import { StatusBarPortalContext } from "./multi-pane/statusBarPortal";
import {
  closeLeaf,
  findLeafByContent,
  findSplitByContent,
  listLeaves,
  makeLeaf,
  splitLeaf,
  type PaneTree,
  type SplitOrientation,
} from "./multi-pane/tree";
import { useScratchpadSize } from "./multi-pane/useScratchpadSize";
import { PaneProvider, type PaneActions } from "./pane/context";

const MIN_PANE_PX = 150;

const rectStyle = (rect: Rect) => ({
  top: `${rect.top}%`,
  left: `${rect.left}%`,
  width: `${rect.width}%`,
  height: `${rect.height}%`,
});

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export function MultiPaneShell() {
  const [tree, setTree] = useSessionStorageState<PaneTree>("multi-pane:tree:v1", makeLeaf);
  const [sizes, setSizes] = useSessionStorageState<Record<string, number>>("multi-pane:sizes:v1", () => ({}));
  const [statusBarSlot, setStatusBarSlot] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { fraction: scratchpadFraction, save: saveScratchpadFraction } = useScratchpadSize();

  const handleSplit = useCallback(
    (leafId: string, orientation: SplitOrientation) => {
      setTree((t) => splitLeaf(t, leafId, orientation));
    },
    [setTree],
  );
  const handleClose = useCallback(
    (leafId: string) => {
      setTree((t) => closeLeaf(t, leafId) ?? t);
    },
    [setTree],
  );
  const handleToggleScratchpad = useCallback(
    (leafId: string) => {
      setTree((t) => {
        const existing = findLeafByContent(t, SCRATCHPAD_MODULE);
        if (existing) return closeLeaf(t, existing) ?? t;
        const next = splitLeaf(t, leafId, "vertical", SCRATCHPAD_MODULE);
        const splitId = findSplitByContent(next, SCRATCHPAD_MODULE);
        if (splitId && scratchpadFraction !== undefined) setSizes((s) => ({ ...s, [splitId]: scratchpadFraction }));
        return next;
      });
    },
    [setTree, setSizes, scratchpadFraction],
  );
  const commitSize = useCallback(
    (splitId: string, fraction: number) => {
      setSizes((s) => ({ ...s, [splitId]: fraction }));
      if (splitId === findSplitByContent(tree, SCRATCHPAD_MODULE)) saveScratchpadFraction(fraction);
    },
    [setSizes, tree, saveScratchpadFraction],
  );

  const { setPanes, setCommands } = useLayoutProducer();
  useEffect(() => setPanes(listLeaves(tree)), [tree, setPanes]);
  useEffect(() => {
    setCommands({ split: handleSplit, close: handleClose });
    return () => setCommands(null);
  }, [setCommands, handleSplit, handleClose]);

  const canClose = tree.kind === "split";
  const hasScratchpad = findLeafByContent(tree, SCRATCHPAD_MODULE) !== null;
  const { panes, dividers } = computeLayout(tree, sizes);
  const primaryPaneId = panes[0]?.id ?? null;
  const portal = useMemo(() => ({ slot: statusBarSlot, primaryPaneId }), [statusBarSlot, primaryPaneId]);

  return (
    <StatusBarPortalContext.Provider value={portal}>
      <div className="flex h-full w-full flex-col max-md:pb-[calc(48px+env(safe-area-inset-bottom))]">
        <div ref={containerRef} className="relative min-h-0 flex-1">
          {panes.map((pane) => {
            const actions: PaneActions = {
              splitCurrent: (orientation) => handleSplit(pane.id, orientation),
              closeCurrent: () => handleClose(pane.id),
              canClose,
              hasScratchpad,
              toggleScratchpad: () => handleToggleScratchpad(pane.id),
            };
            return (
              <div key={pane.id} className="absolute overflow-hidden" style={rectStyle(pane.rect)}>
                <PaneProvider paneId={pane.id} actions={actions}>
                  <ArtisModuleHost paneId={pane.id} content={pane.content ?? MARKET_GRID_MODULE} />
                </PaneProvider>
              </div>
            );
          })}
          {dividers.map((divider) => (
            <Divider key={divider.splitId} info={divider} containerRef={containerRef} onCommit={commitSize} />
          ))}
        </div>
        {/* Portaled charts and the desktop bar. Padding on the shell keeps them above the phone nav. */}
        <div ref={setStatusBarSlot} className="flex shrink-0 flex-col" />
      </div>
    </StatusBarPortalContext.Provider>
  );
}

export function Divider({
  info,
  containerRef,
  onCommit,
}: {
  info: DividerInfo;
  containerRef: { current: HTMLDivElement | null };
  onCommit: (splitId: string, fraction: number) => void;
}) {
  const [preview, setPreview] = useState<number | null>(null);
  const horizontal = info.orientation === "horizontal";
  const { splitRect } = info;
  const fraction = preview ?? info.fraction;
  const boundary = horizontal
    ? splitRect.left + splitRect.width * fraction
    : splitRect.top + splitRect.height * fraction;

  const onMove = (e: ReactPointerEvent) => {
    const el = containerRef.current;
    if (el === null) return;
    const box = el.getBoundingClientRect();
    if (horizontal) {
      const pct = ((e.clientX - box.left) / box.width) * 100;
      const minF = MIN_PANE_PX / ((box.width * splitRect.width) / 100);
      setPreview(clamp((pct - splitRect.left) / splitRect.width, minF, 1 - minF));
    } else {
      const pct = ((e.clientY - box.top) / box.height) * 100;
      const minF = MIN_PANE_PX / ((box.height * splitRect.height) / 100);
      setPreview(clamp((pct - splitRect.top) / splitRect.height, minF, 1 - minF));
    }
  };

  const style = horizontal
    ? { left: `${boundary}%`, top: `${splitRect.top}%`, height: `${splitRect.height}%`, width: "5px" }
    : { top: `${boundary}%`, left: `${splitRect.left}%`, width: `${splitRect.width}%`, height: "5px" };

  return (
    <div
      data-artis-ui
      className={cn(
        "absolute z-10 touch-none bg-[var(--colour-border-dark)] transition-colors hover:bg-brand",
        horizontal ? "-translate-x-1/2 cursor-col-resize" : "-translate-y-1/2 cursor-row-resize",
      )}
      style={style}
      onPointerDown={(e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setPreview(info.fraction);
      }}
      onPointerMove={(e) => {
        if (preview !== null) onMove(e);
      }}
      onPointerUp={(e) => {
        e.currentTarget.releasePointerCapture(e.pointerId);
        if (preview !== null) onCommit(info.splitId, preview);
        setPreview(null);
      }}
    />
  );
}
